import { Box, Chip, ListItem, ListItemText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { EventRecord, TimelineItemProps, useEventLabel } from '@react-admin/ra-audit-log';
import classnames from 'classnames';
import { format, toZonedTime } from 'date-fns-tz';
import { useRecordContext, useTranslate } from 'react-admin';

import { BOLogAction, BOLogPayload } from '@Common/types/bo/bo_logs';

export function TimelineItem(props: TimelineItemProps & { showExtraInfo?: boolean }) {
  const {
    className,
    showExtraInfo: forceShowExtraInfo = false,
    classes: _classesOverride,
    link: _link,
    record: parentRecord,
    ...rest
  } = props;
  const classes = useStyles(props);
  const translate = useTranslate();
  const record = useRecordContext<EventRecord & { extra_info?: string | null }>(parentRecord);
  const actionLabel = useEventLabel({ record, variant: 'inline' });

  if (!record) {
    return null;
  }

  const extraInfoLogResources: BOLogPayload['resource'][] = ['b2b_individual_operation'];
  const restrictionLogResources: BOLogPayload['resource'][] = ['b2b_employee', 'b2b_company'];
  const restrictionActions: BOLogAction[] = [
    'restriction_added',
    'restriction_removed',
    'restriction_edited',
  ];
  const showExtraInfo =
    forceShowExtraInfo ||
    extraInfoLogResources.includes(record.resource as BOLogPayload['resource']) ||
    (restrictionLogResources.includes(record.resource as BOLogPayload['resource']) &&
      restrictionActions.includes(record.action as BOLogAction)) ||
    (record.resource === 'user' && record.action === 'edit');

  return (
    <ListItem role="listitem" className={classnames(classes.root, className)} {...rest}>
      <ListItemText
        primary={
          <Box className={classes.content}>
            <Box className={classes.time}>
              {`${format(toZonedTime(new Date(record.date), 'UTC'), 'HH:mm:ss')} UTC`}
            </Box>
            <Chip
              className={classes.author}
              label={record.author.fullName || translate('ra-audit-log.author', record.author)}
            />
            <Box className={classes.action}>{actionLabel}</Box>
            {record.extra_info && showExtraInfo && (
              <Box className={classes.extraInfo}>{record.extra_info}</Box>
            )}
          </Box>
        }
      />
    </ListItem>
  );
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      paddingTop: 0,
      paddingBottom: 0,
    },
    content: {
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'column',
      rowGap: '5px',
      wordBreak: 'break-word',
    },
    author: {
      width: 'fit-content',
    },
    action: { marginLeft: '3px' },
    extraInfo: {
      marginLeft: '3px',
      color: '#666666',
      fontSize: 'small',
      whiteSpace: 'pre-line',
    },
    time: { color: '#999999', marginLeft: '3px', fontSize: 'small' },
  }),
  {
    name: 'RaTimelineItem',
  },
);
